import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useWorkspace } from "@/context/WorkspaceContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { inr } from "@/lib/format";
import { toast } from "sonner";
import { UploadCloud, X, AlertCircle, CheckCircle2 } from "lucide-react";

const CHANNELS = [
  { value: "shopify", label: "Shopify" },
  { value: "amazon", label: "Amazon" },
  { value: "flipkart", label: "Flipkart" },
  { value: "offline", label: "Offline / B2B" },
  { value: "other", label: "Other" },
];

export default function ReturnsUploadDialog({ open, onOpenChange, onCommitted }) {
  const { current } = useWorkspace();
  const [files, setFiles] = useState([]);
  const [channel, setChannel] = useState("shopify");
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [committing, setCommitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setFiles([]); setChannel("shopify"); setPreview(null);
  }, [open]);

  const onFileChange = (e) => {
    const list = Array.from(e.target.files || []);
    setFiles([...files, ...list]);
  };
  const removeFile = (i) => setFiles(files.filter((_, idx) => idx !== i));

  const upload = async () => {
    if (!current || files.length === 0) return;
    setUploading(true);
    try {
      const fd = new FormData();
      for (const f of files) fd.append("files", f);
      const { data } = await api.post("/returns/upload", fd, {
        params: { workspace_id: current.id, channel },
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally { setUploading(false); }
  };

  const commit = async () => {
    setCommitting(true);
    try {
      const { data } = await api.post("/returns/upload/commit", { upload_id: preview.upload_id, skip_unmatched: true });
      toast.success(`Imported ${data.inserted} return(s) across ${data.affected_dates.length} day(s)`);
      onCommitted?.(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Commit failed");
    } finally { setCommitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="returns-upload-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Upload Returns / RTO File</DialogTitle>
          <p className="text-xs text-muted-foreground mt-1">Supports Shopify Refund Export &amp; Shipping NDR reports. Rows are matched to existing orders by Order ID.</p>
        </DialogHeader>

        {!preview && (
          <div className="space-y-5">
            <div>
              <Label className="overline">Sales channel these returns belong to</Label>
              <Select value={channel} onValueChange={setChannel}>
                <SelectTrigger className="rounded-none mt-2 h-11" data-testid="returns-channel"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CHANNELS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="overline">CSV Files</Label>
              <label htmlFor="returns-file-input" className="mt-2 border-2 border-dashed border-border p-8 flex flex-col items-center justify-center cursor-pointer hover:bg-accent transition-colors" data-testid="returns-dropzone">
                <UploadCloud className="h-8 w-8 text-muted-foreground mb-2" />
                <div className="text-sm">Click to add refund / RTO CSV files</div>
                <div className="text-xs text-muted-foreground mt-1">Detects columns like Order ID, Refunded Amount, RTO Fee, Condition</div>
                <input id="returns-file-input" type="file" accept=".csv,text/csv" multiple className="hidden" onChange={onFileChange} data-testid="returns-file-input" />
              </label>
              {files.length > 0 && (
                <div className="mt-3 space-y-1" data-testid="returns-file-list">
                  {files.map((f, i) => (
                    <div key={i} className="flex items-center justify-between border border-border px-3 py-2">
                      <div className="text-sm font-mono truncate">{f.name}</div>
                      <button onClick={() => removeFile(i)} className="text-muted-foreground hover:text-foreground"><X className="h-3.5 w-3.5" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-none">Cancel</Button>
              <Button onClick={upload} disabled={uploading || files.length === 0} className="rounded-none" data-testid="returns-parse-btn">
                {uploading ? "Parsing…" : "Parse & preview"}
              </Button>
            </DialogFooter>
          </div>
        )}

        {preview && (
          <div className="space-y-5">
            <div className="grid grid-cols-4 grid-skeleton border border-border" data-testid="returns-preview">
              <div className="p-4 min-h-[100px]">
                <div className="overline text-muted-foreground">Rows parsed</div>
                <div className="font-mono text-2xl font-semibold mt-2">{preview.row_count}</div>
                <div className="text-[10px] font-mono text-muted-foreground mt-1">{preview.matched_count} matched · {preview.row_count - preview.matched_count} unmatched</div>
              </div>
              <div className="p-4 min-h-[100px]">
                <div className="overline text-muted-foreground">Refund total</div>
                <div className="font-mono text-2xl font-semibold mt-2 text-[hsl(var(--negative))]">{inr(preview.totals.refund_amount, { decimals: 0 })}</div>
              </div>
              <div className="p-4 min-h-[100px]">
                <div className="overline text-muted-foreground">Reverse ship + RTO</div>
                <div className="font-mono text-2xl font-semibold mt-2 text-[hsl(var(--negative))]">{inr(preview.totals.reverse_shipping + preview.totals.rto_fee, { decimals: 0 })}</div>
              </div>
              <div className="p-4 min-h-[100px]">
                <div className="overline text-muted-foreground">Conditions</div>
                <div className="mt-2 space-y-0.5 font-mono text-xs">
                  <div>Restocked · {preview.conditions.restocked || 0}</div>
                  <div>Damaged · {preview.conditions.damaged || 0}</div>
                  <div>Other · {preview.conditions.other || 0}</div>
                </div>
              </div>
            </div>

            {preview.unmatched_orders.length > 0 && (
              <div className="border border-border p-4 flex items-start gap-3 bg-accent/40" data-testid="returns-unmatched">
                <AlertCircle className="h-5 w-5 text-[hsl(var(--negative))] shrink-0 mt-0.5" />
                <div className="text-xs">
                  <div className="font-medium">{preview.unmatched_orders.length} order ID(s) not found in this workspace — they'll be skipped on commit.</div>
                  <div className="mt-1 text-muted-foreground font-mono truncate">{preview.unmatched_orders.slice(0, 8).join(", ")}{preview.unmatched_orders.length > 8 ? " …" : ""}</div>
                  <div className="mt-1 text-muted-foreground">Import the corresponding orders first (Orders &amp; Uploads → Upload CSV), then re-upload the returns file.</div>
                </div>
              </div>
            )}

            {preview.unmatched_orders.length === 0 && preview.matched_count > 0 && (
              <div className="border border-border p-4 flex items-center gap-3">
                <CheckCircle2 className="h-5 w-5 text-[hsl(var(--positive))] shrink-0" />
                <div className="text-sm">All {preview.matched_count} return rows matched to existing orders.</div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setPreview(null)} className="rounded-none">Back</Button>
              <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-none">Cancel</Button>
              <Button onClick={commit} disabled={committing || preview.matched_count === 0} className="rounded-none" data-testid="returns-commit-btn">
                {committing ? "Importing…" : `Commit ${preview.matched_count} return(s)`}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
